import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bell, FileText, Wrench, CreditCard, Shield,
  AlertTriangle, Calendar, CheckCheck, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const NOTIF_ICONS = {
  estimate_accepted: { icon: FileText, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  estimate_rejected: { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' },
  job_assigned: { icon: Wrench, color: 'text-blue-500', bg: 'bg-blue-50' },
  payment_received: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-50' },
  permit_approved: { icon: Shield, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  permit_denied: { icon: Shield, color: 'text-red-500', bg: 'bg-red-50' },
  maintenance_due: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
  booking_new: { icon: Calendar, color: 'text-cyan-500', bg: 'bg-cyan-50' },
  schedule_reminder: { icon: Calendar, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  inventory_low: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
};

export default function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('notifications.justNow');
    if (mins < 60) return t('notifications.minutesAgo', { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('notifications.hoursAgo', { n: hours, s: hours > 1 ? 's' : '' });
    const days = Math.floor(hours / 24);
    return t('notifications.daysAgo', { n: days, s: days > 1 ? 's' : '' });
  };

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const params = {};
        if (filter === 'unread') params.is_read = false;
        if (filter === 'read') params.is_read = true;
        const res = await api.get('/notifications', { params });
        setNotifications(res.data?.items || res.data || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchNotifs();
  }, [filter]);

  const markAsRead = async (notif) => {
    try {
      await api.put(`/notifications/${notif.id}/read`);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      if (notif.related_entity_type === 'job') navigate('/jobs');
      else if (notif.related_entity_type === 'estimate') navigate('/estimates');
      else if (notif.related_entity_type === 'project') navigate('/projects');
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success(t('notifications.markAllRead'));
    } catch { toast.error(t('errors.generic')); }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div data-testid="notifications-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Bell className="w-6 h-6 text-[#3399FF]" /> {t('notifications.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{unreadCount} {t('notifications.unreadFilter').toLowerCase()}</p>
        </div>
        {unreadCount > 0 && (
          <Button data-testid="mark-all-read-page" onClick={markAllRead} variant="outline" className="border-gray-200">
            <CheckCheck className="w-4 h-4 mr-2" /> {t('notifications.markAllRead')}
          </Button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white border border-gray-200 h-9">
          <TabsTrigger value="all" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">{t('notifications.allFilter')}</TabsTrigger>
          <TabsTrigger value="unread" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">{t('notifications.unreadFilter')}</TabsTrigger>
          <TabsTrigger value="read" className="text-xs px-3 data-[state=active]:bg-[#003366] data-[state=active]:text-white">{t('notifications.readFilter')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
      ) : notifications.length === 0 ? (
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardContent className="py-16 text-center">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">{t('notifications.noNotifications')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map((notif) => {
            const config = NOTIF_ICONS[notif.type] || { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-50' };
            const NotifIcon = config.icon;
            return (
              <Card
                key={notif.id}
                data-testid={`notif-card-${notif.id}`}
                onClick={() => markAsRead(notif)}
                className={`bg-white border shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 ${
                  !notif.is_read ? 'border-[#3399FF]/30 bg-blue-50/20' : 'border-gray-100'
                }`}
              >
                <CardContent className="p-4 flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <NotifIcon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-sm font-medium ${!notif.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notif.title}
                        </p>
                        <p className="text-sm text-gray-500 mt-0.5">{notif.message}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {timeAgo(notif.created_at)}
                        </span>
                        {!notif.is_read && <div className="w-2.5 h-2.5 bg-[#3399FF] rounded-full" />}
                      </div>
                    </div>
                    {notif.priority === 'urgent' && (
                      <span className="inline-flex items-center mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-red-50 text-red-600">
                        {t('jobs.priorities.urgent')}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
